import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import App from "./App";
import Signup from "./auth/Signup";
import Signin from "./views/Signin/Signin";
import TermsAndConditions from "./auth/TermsAndConditions";
import Eula from "./auth/Eula";
import Activate from "./auth/Activate";
import Profile from "./core/Profile";
import Test from "./core/Test";
import PrivateRoute from "./auth/PrivateRoute";
import Forgot from "./auth/Forgot";
import Reset from "./auth/Reset";
import DashboardPage from "./views/Dashboard/Dashboard.js";
import UsersPage from "./views/Users/Users";
import VersionsPage from "./views/Versions/Versions";
import UserDetails from "./views/UserDetails/UserDetails";
import OfferDetails from "./views/OfferDetails/OfferDetails";
import OfferCreate from "./views/OfferCreate/OfferCreate";
import RequestsPage from "./views/Requests/Requests";
import ActiveRequests from "./views/ActiveRequests/ActiveRequests";
import RequestDetails from "./views/RequestDetails/RequestDetails";
import OffersPage from "./views/Offers/Offers";
import CreditPage from "./views/Credit/Credit";
import SettingsPage from "./views/Settings/Settings";
import NotificationsPage from "./views/Notifications/Notifications";
import OperatersPage from "./views/Operaters/Operaters";
import SentOffers from "./views/SentOffers/SentOffers";
import AcceptedOffers from "./views/AcceptedOffers/AcceptedOffers";
import RequestCreateOffer from "./views/RequestCreateOffer/RequestCreateOffer";
import Cars from "./views/Cars/Cars";
import { isAuth } from "./auth/helpers";
import Forbidden from "./auth/Forbidden";
import UserRoles from "./auth/UserRoles";
import UserCreate from "./views/UserCreate/UserCreate";
import HistoryLogs from "./views/HistoryLogs/HistoryLogs";
import HistoryLogDetails from "./views/HistoryLogDetails/HistoryLogDetails";
import CarDetails from "./views/CarDetails/CarDetails";
import FilterDetails from "./views/FiltersDetails/FilterDetails";
import TransactionsPage from "./views/Transactions/Transactions";
import TransactionDetails from "./views/TransactionDetails/TransactionDetails";
import Filters from "./views/Filters/Filters";
import FilterUsers from "./views/Filters/FilterUsers";
import LandingPage from "./views/LandingPage/LandingPage";
import QR from "./views/QR/QR";
import QRPartner from "./views/QRPartner/QRPartner";
import VersionCreate from "./views/VersionCreate/VersionCreate";
import VersionDetails from "./views/VersionDetails/VersionDetails";
import Support from "./views/Support/Support";
import WelcomeSeller from "./BuyerSellerStack/SellerStack/seller-views/WelcomeSeller.js";
import OrderPart from "./BuyerSellerStack/BuyerStack/buyer-views/OrderPart.js";

const AppRoutes = () => {
    return (
        <Routes>
            {/* Root route: redirect based on authentication */}
            <Route
                path="/"
                element={
                    isAuth() ? (
                        <Navigate to="/admin/dashboard" replace />
                    ) : (
                        <Navigate to="/landing" replace />
                    )
                }
            />

            {/* Public Routes */}
            <Route path="/signup" element={<Signup />} />
            <Route path="/landing" element={<LandingPage />} />
            <Route path="/support" element={<Support />} />
            <Route path="/signin" element={<Signin />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/eula" element={<Eula />} />
            <Route path="/QR" element={<QR />} />
            <Route path="/QRPartner" element={<QRPartner />} />
            <Route path="/test" element={<Test />} />
            <Route path="/buyer-welcome" element={<OrderPart />} />
            <Route path="/seller-welcome" element={<WelcomeSeller />} />

            {/* Protected Routes */}
            <Route
                path="/home"
                element={
                    <PrivateRoute>
                        <App />
                    </PrivateRoute>
                }
            />
            <Route
                path="/admin/users/create"
                element={
                    <PrivateRoute roles={[UserRoles.ADMIN, UserRoles.OPERATER]}>
                        <UserCreate />
                    </PrivateRoute>
                }
            />
            <Route
                path="/admin/versions/create"
                element={
                    <PrivateRoute roles={[UserRoles.ADMIN, UserRoles.OPERATER]}>
                        <VersionCreate />
                    </PrivateRoute>
                }
            />
            <Route
                path="/admin/dashboard"
                element={
                    <PrivateRoute
                        roles={[UserRoles.ADMIN, UserRoles.OPERATER, UserRoles.USER]}
                    >
                        <DashboardPage />
                    </PrivateRoute>
                }
            />
            <Route
                path="/admin/users"
                element={
                    <PrivateRoute roles={[UserRoles.ADMIN, UserRoles.OPERATER]}>
                        <UsersPage />
                    </PrivateRoute>
                }
            />
            <Route
                path="/admin/requests"
                element={
                    <PrivateRoute roles={[UserRoles.ADMIN, UserRoles.OPERATER]}>
                        <RequestsPage />
                    </PrivateRoute>
                }
            />
            <Route
                path="/admin/offers"
                element={
                    <PrivateRoute roles={[UserRoles.ADMIN, UserRoles.OPERATER]}>
                        <OffersPage />
                    </PrivateRoute>
                }
            />
            <Route
                path="/admin/offers/create"
                element={
                    <PrivateRoute roles={[UserRoles.ADMIN, UserRoles.OPERATER]}>
                        <OfferCreate />
                    </PrivateRoute>
                }
            />
            <Route
                path="/admin/credit"
                element={
                    <PrivateRoute roles={[UserRoles.ADMIN]}>
                        <CreditPage />
                    </PrivateRoute>
                }
            />
            <Route
                path="/admin/settings"
                element={
                    <PrivateRoute roles={[UserRoles.ADMIN]}>
                        <SettingsPage />
                    </PrivateRoute>
                }
            />
            <Route
                path="/admin/versions"
                element={
                    <PrivateRoute roles={[UserRoles.ADMIN]}>
                        <VersionsPage />
                    </PrivateRoute>
                }
            />
            <Route
                path="/admin/transactions"
                element={
                    <PrivateRoute roles={[UserRoles.ADMIN]}>
                        <TransactionsPage />
                    </PrivateRoute>
                }
            />
            <Route
                path="/admin/transactions/:id"
                element={
                    <PrivateRoute roles={[UserRoles.ADMIN]}>
                        <TransactionDetails />
                    </PrivateRoute>
                }
            />
            <Route
                path="/admin/notifications"
                element={
                    <PrivateRoute roles={[UserRoles.ADMIN]}>
                        <NotificationsPage />
                    </PrivateRoute>
                }
            />
            <Route
                path="/admin/operaters"
                element={
                    <PrivateRoute roles={[UserRoles.ADMIN]}>
                        <OperatersPage />
                    </PrivateRoute>
                }
            />
            <Route
                path="/admin/users/:id"
                element={
                    <PrivateRoute roles={[UserRoles.ADMIN, UserRoles.OPERATER]}>
                        <UserDetails />
                    </PrivateRoute>
                }
            />
            <Route
                path="/admin/versions/:id"
                element={
                    <PrivateRoute roles={[UserRoles.ADMIN, UserRoles.OPERATER]}>
                        <VersionDetails />
                    </PrivateRoute>
                }
            />
            <Route
                path="/admin/offers/:id"
                element={
                    <PrivateRoute roles={[UserRoles.ADMIN, UserRoles.OPERATER]}>
                        <OfferDetails />
                    </PrivateRoute>
                }
            />
            <Route
                path="/admin/requests/:id"
                element={
                    <PrivateRoute roles={[UserRoles.ADMIN, UserRoles.OPERATER]}>
                        <RequestDetails />
                    </PrivateRoute>
                }
            />
            <Route
                path="/admin/activerequests"
                element={
                    <PrivateRoute roles={[UserRoles.ADMIN, UserRoles.OPERATER]}>
                        <ActiveRequests />
                    </PrivateRoute>
                }
            />
            <Route
                path="/admin/sentoffers"
                element={
                    <PrivateRoute roles={[UserRoles.ADMIN, UserRoles.OPERATER]}>
                        <SentOffers />
                    </PrivateRoute>
                }
            />
            <Route
                path="/admin/acceptedoffers"
                element={
                    <PrivateRoute roles={[UserRoles.ADMIN, UserRoles.OPERATER]}>
                        <AcceptedOffers />
                    </PrivateRoute>
                }
            />
            <Route
                path="/admin/requestcreateoffer/:id"
                element={
                    <PrivateRoute roles={[UserRoles.ADMIN, UserRoles.OPERATER]}>
                        <RequestCreateOffer />
                    </PrivateRoute>
                }
            />
            <Route
                path="/admin/usercars"
                element={
                    <PrivateRoute roles={[UserRoles.ADMIN]}>
                        <Cars />
                    </PrivateRoute>
                }
            />
            <Route
                path="/admin/userfilters"
                element={
                    <PrivateRoute roles={[UserRoles.ADMIN, UserRoles.OPERATER]}>
                        <Filters />
                    </PrivateRoute>
                }
            />
            <Route
                path="/admin/history-logs"
                element={
                    <PrivateRoute roles={[UserRoles.ADMIN, UserRoles.OPERATER]}>
                        <HistoryLogs />
                    </PrivateRoute>
                }
            />
            <Route
                path="/admin/history-logs/:id"
                element={
                    <PrivateRoute roles={[UserRoles.ADMIN, UserRoles.OPERATER]}>
                        <HistoryLogDetails />
                    </PrivateRoute>
                }
            />
            <Route
                path="/admin/usercars/:id"
                element={
                    <PrivateRoute roles={[UserRoles.ADMIN]}>
                        <CarDetails />
                    </PrivateRoute>
                }
            />
            <Route
                path="/admin/userfilters/:id"
                element={
                    <PrivateRoute roles={[UserRoles.ADMIN]}>
                        <FilterDetails />
                    </PrivateRoute>
                }
            />
            <Route
                path="/admin/userfilters/:name/users"
                element={
                    <PrivateRoute roles={[UserRoles.ADMIN, UserRoles.OPERATER]}>
                        <FilterUsers />
                    </PrivateRoute>
                }
            />

            {/* Seller Routes */}
            <Route
                path="/seller/welcome"
                element={
                    <PrivateRoute roles={[UserRoles.ADMIN, UserRoles.SELLER]}>
                        <WelcomeSeller />
                    </PrivateRoute>
                }
            />

            {/* Buyer Routes */}
            <Route
                path="/buyer/order-part"
                element={
                    <PrivateRoute roles={[UserRoles.ADMIN, UserRoles.USER]}>
                        <OrderPart />
                    </PrivateRoute>
                }
            />

            {/* Other Routes */}
            <Route path="/auth/activate/:token" element={<Activate />} />
            <Route
                path="/profile"
                element={
                    <PrivateRoute>
                        <Profile />
                    </PrivateRoute>
                }
            />
            <Route path="/auth/password/forgot" element={<Forgot />} />
            <Route path="/auth/password/reset/:token" element={<Reset />} />
            <Route path="/makes" element={<App />} />
            <Route path="/forbidden" element={<Forbidden />} />
        </Routes>
    );
};

export default AppRoutes;
